import { useCallback, useEffect, useRef, useState } from "react";
import { getMyStatement } from "../api/stripeApi/statement";
import { readStatement } from "../utils/stripeStatement";

/**
 * Fetches the authenticated user's ledger statement (`GET payment/me/statement`)
 * and exposes the server-computed totals (earned/spent/withdrawn/pending) plus the
 * flat, newest-first `entries` the wallet screen groups into its tabbed list. The
 * plain user payload carries no wallet object, so the wallet screen reads its money
 * from here (matching the mobile client). Guards against setting state after
 * unmount. Enable it only under the Stripe flag from the caller.
 *
 * Refetches when the tab becomes visible again, so money that moved elsewhere (a
 * payout, a contract funded in another tab) is reflected without a hard reload.
 * This is the web counterpart of mobile's refetch-on-screen-focus.
 */
export const useStripeStatement = (enabled = true) => {
  const [statement, setStatement] = useState(() => readStatement(null));
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMyStatement();
      if (mountedRef.current) {
        setStatement(readStatement(data));
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(e);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) {
      setLoading(false);
      return () => {
        mountedRef.current = false;
      };
    }
    load();
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        load();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      mountedRef.current = false;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, load]);

  return { ...statement, loading, error, refetch: load };
};
